import type { MyIpItem } from './types';
import { getMyIpMediaUrl } from './mediaUrl';
import { isMyIpMaterialRemoved } from './materialCollection';
import { isMyIpResourceActive } from './resourceSlots';

export interface MyIpMediaReference {
  /** 仅记录服务端提供的资产身份；直链素材可以没有资产 ID。 */
  assetId?: string;
  url?: string;
  thumbnailUrl?: string;
  /** 仅承接服务端明确返回的带水印地址。 */
  watermarkUrl?: string;
  watermarkCoverUrl?: string;
  type: 'image' | 'video' | 'audio' | 'unknown';
  role: string;
  name: string;
  voiceId?: string;
  available?: boolean;
  fileExtension?: string;
  duration?: number;
  fileSize?: number;
  width?: number;
  height?: number;
}

/** 媒体类型只读声明值，不能通过临时 URL 后缀推断服务端资源类型。 */
function mediaType(value: unknown): MyIpMediaReference['type'] {
  return value === 'image' || value === 'video' || value === 'audio' ? value : 'unknown';
}

/** 渲染去重键与持久化资产身份分开，直链仅用于当前预览项的局部身份。 */
export function getMyIpMediaReferenceKey(reference: MyIpMediaReference): string {
  return JSON.stringify([reference.assetId ? 'asset' : 'url', reference.assetId || reference.url, reference.role]);
}

/** 授权响应明确不可用时禁止任何旧直链或外部地址表恢复预览。 */
export function getMyIpMediaReferenceUrl(reference: MyIpMediaReference, urls: Record<string, string> = {}): string | undefined {
  if (reference.available === false) return undefined;
  return getMyIpMediaUrl(reference.assetId ? urls[reference.assetId] : undefined) || getMyIpMediaUrl(reference.url);
}

/** 仅用当前 IP 内相同资产身份补齐媒体；新签名优先，明确撤回时清空所有播放/下载地址。 */
export function resolveMyIpMediaReference(reference: MyIpMediaReference, item: MyIpItem): MyIpMediaReference {
  if (!reference.assetId) return reference;
  const matches = item.resources.filter((resource) => resource.assetId === reference.assetId);
  const resources = matches.filter((resource) => (
    !['disabled', 'deleted', 'removed'].includes(resource.status)
    && !isMyIpMaterialRemoved(item.profile, { asset_id: resource.assetId })));
  // 关系失效不代表资产全局失效，但显式 available=false 不能被关系过滤丢掉。
  const resource = matches.find((entry) => entry.available === false)
    || resources.find((entry) => getMyIpMediaUrl(entry.url)) || resources[0];
  if (!resource) return reference;
  const url = getMyIpMediaUrl(resource.url);
  const changedUrl = Boolean(url && url !== getMyIpMediaUrl(reference.url));
  return {
    ...reference,
    type: mediaType(resource.type) === 'unknown' ? reference.type : mediaType(resource.type),
    name: resource.name || reference.name,
    ...(resource.voiceId === undefined ? {} : { voiceId: resource.voiceId }),
    ...(resource.available === undefined ? {} : { available: resource.available }),
    ...(resource.fileExtension === undefined ? {} : { fileExtension: resource.fileExtension }),
    ...(resource.duration === undefined ? {} : { duration: resource.duration }),
    ...(resource.fileSize === undefined ? {} : { fileSize: resource.fileSize }),
    ...(resource.width === undefined ? {} : { width: resource.width }),
    ...(resource.height === undefined ? {} : { height: resource.height }),
    ...(resource.available === false ? { url: undefined, thumbnailUrl: undefined, watermarkUrl: undefined, watermarkCoverUrl: undefined } : {
      ...(url ? { url } : {}),
      // 主地址更新后旧缩略图/水印签名不再配对，只有本次明确返回的地址可补回。
      ...(changedUrl ? { thumbnailUrl: undefined, watermarkUrl: undefined, watermarkCoverUrl: undefined } : {}),
      ...(getMyIpMediaUrl(resource.thumbnailUrl) ? { thumbnailUrl: getMyIpMediaUrl(resource.thumbnailUrl) } : {}),
      ...(getMyIpMediaUrl(resource.watermarkUrl) ? { watermarkUrl: getMyIpMediaUrl(resource.watermarkUrl) } : {}),
      ...(getMyIpMediaUrl(resource.watermarkCoverUrl) ? { watermarkCoverUrl: getMyIpMediaUrl(resource.watermarkCoverUrl) } : {}),
    }),
  };
}

/** 媒体槽位只投影 resources；存量 profile.materials 不得恢复已删除的槽位。 */
export function getMyIpMediaReferences(item: MyIpItem) {
  const references: MyIpMediaReference[] = item.resources.filter(isMyIpResourceActive).map((resource) => ({
    assetId: getMyIpMediaUrl(resource.assetId) ? undefined : resource.assetId || undefined,
    // assetId 只用于资源关系匹配，展示和播放必须来自服务端明确返回的 url 字段。
    url: resource.available === false ? undefined : getMyIpMediaUrl(resource.url),
    thumbnailUrl: resource.available === false ? undefined : getMyIpMediaUrl(resource.thumbnailUrl),
    watermarkUrl: resource.available === false ? undefined : getMyIpMediaUrl(resource.watermarkUrl),
    watermarkCoverUrl: resource.available === false ? undefined : getMyIpMediaUrl(resource.watermarkCoverUrl),
    type: mediaType(resource.type),
    role: resource.role,
    name: resource.name || '',
    ...(resource.voiceId === undefined ? {} : { voiceId: resource.voiceId }),
    available: resource.available,
    fileExtension: resource.fileExtension,
    duration: resource.duration,
    fileSize: resource.fileSize,
    width: resource.width,
    height: resource.height,
  }));
  return { unsupported: references.some((entry) => entry.type === 'unknown'), references };
}
